import React, { useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from 'src/component/ds/ui/inputGroup';
import type { InputProps } from 'src/component/ds/ui/input';
import { cn } from 'src/component/ds/utils';

export interface SearchInputProps extends Omit<InputProps, 'value' | 'onChange' | 'size' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name of the clear button. */
  clearLabel: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onValueChange, clearLabel, className, onKeyDown, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    };

    const clear = () => {
      onValueChange('');
      inputRef.current?.focus();
    };

    return (
      <InputGroup
        className={cn(
          'ds-h-8 ds-rounded ds-bg-background ds-bg-[linear-gradient(var(--ds-tint),var(--ds-tint))]',
          'has-[>[data-align=inline-end]]:[&>input]:ds-pr-1.5',
          className
        )}
      >
        <InputGroupInput
          ref={setRefs}
          type='search'
          size='sm'
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value) {
              e.preventDefault();
              clear();
            }
            onKeyDown?.(e);
          }}
          className='[&::-webkit-search-cancel-button]:ds-hidden'
          {...props}
        />
        <InputGroupAddon className='ds-pl-2.5'>
          <SearchOutlinedIcon sx={{ fontSize: 16 }} aria-hidden='true' />
        </InputGroupAddon>
        {value && (
          <InputGroupAddon align='inline-end' className='ds-pr-2 has-[>button]:ds-mr-0'>
            <InputGroupButton
              size='icon-xs'
              aria-label={clearLabel}
              onClick={clear}
              className='ds-size-5 ds-text-black dark:ds-text-foreground'
            >
              <CloseIcon sx={{ fontSize: 16 }} aria-hidden='true' />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
    );
  }
);
SearchInput.displayName = 'SearchInput';
